import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab, Typography, Grid, Paper, Button as MuiButton, TextField as MuiTextField, Box as MuiBox, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, InputAdornment, Modal } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import nodeAxiosFirebase from '../../../utils/nodeAxiosFirebase';
import Button from '../../general-components/Button';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SettingsIcon from '@mui/icons-material/Settings';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import Blocks from '../Block';
import Loading from '../../general-components/Loading';

// Standardized interface for connector data
const standardizeQuote = (quote, connector) => {
  if (!quote) return null;

  // Map connector-specific fields to standard format
  const standardQuote = {
    productID: quote.productID || quote.sku || quote.partNumber,
    productDesc: quote.productDesc || quote.description || quote.name,
    brandID: quote.brandID || quote.brand || quote.manufacturer,
    quality: quote.quality || quote.condition || 'Standard',
    price: quote?.price || quote?.unitPrice || quote?.costPrice,
    listPrice: quote.internalPrice || quote.listPrice || quote.retailPrice || quote.price,
    quantityAvail: quote.quantityAvail || quote.stock || quote.availableQuantity,
    orderByTime: quote.orderByTime || quote.cutoffTime || quote.orderDeadline,
    dispatchTime: quote.dispatchTime || quote.shippingTime || quote.deliveryTime,
    connector: connector.name,
    photo: quote.photo || quote.imageUrl,
    rawData: quote
  };

  return standardQuote;
};

// Image Modal Component
const ImageModal = ({ open, onClose, imageUrl, alt }) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="image-modal"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 2,
          borderRadius: 2,
          maxWidth: '90vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            bgcolor: 'background.paper',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={imageUrl}
            alt={alt}
            style={{
              maxWidth: '100%',
              maxHeight: '80vh',
              objectFit: 'contain',
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/assets/chekup/tire.png';
            }}
          />
        </Box>
      </Box>
    </Modal>
  );
};

// Reusable ProductList component
const ProductList = ({
  data,
  cart,
  quantities,
  quotesSearchInput,
  setQuotesSearchInput,
  handleQuantityChange,
  handleAddToCart,
  handleRemoveFromCart,
  businessPreference,
  t
}) => {
  const [selectedImage, setSelectedImage] = useState(null);

  const handleImageClick = (imageUrl, alt) => {
    setSelectedImage({ url: imageUrl, alt });
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  const filteredQuotes = React.useMemo(() => {
    if (!data?.quotes) return [];
    if (!quotesSearchInput) return data.quotes;

    const searchTerm = quotesSearchInput.toLowerCase();
    return data.quotes.filter(quote =>
      quote.productID?.toLowerCase().includes(searchTerm) ||
      quote.productDesc?.toLowerCase().includes(searchTerm) ||
      quote.brandID?.toLowerCase().includes(searchTerm) ||
      quote.quality?.toLowerCase().includes(searchTerm)
    );
  }, [data?.quotes, quotesSearchInput]);

  return (
    <>
      <Grid container spacing={2} sx={{ height: '100%' }}>
        {/* Quotes Section - 2/3 width */}
        <Grid item xs={12} md={cart?.length > 0 ? 8 : 12} sx={{ height: '100%' }}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '40vh' }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
              pb: 1,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '1.05rem' }}>{t('listItems')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="caption" sx={{ width: '100px' }} color="text.secondary">
                  {filteredQuotes?.length} {t('results')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <MuiTextField
                  size="small"
                  placeholder={t('searchQuotes')}
                  value={quotesSearchInput}
                  onChange={(e) => setQuotesSearchInput(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '32px',
                      fontSize: '0.875rem',
                      width: '200px',
                      '& fieldset': {
                        borderColor: 'divider',
                      },
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
            {!data ? (
              <Typography sx={{ p: 2, textAlign: 'center' }}>{t('noQuotesAvailable')}</Typography>
            ) : (
              <TableContainer sx={{ maxHeight: '500px', overflowY: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('photo')}</TableCell>
                      <TableCell>{t('partNumber')}</TableCell>
                      <TableCell>{t('description')}</TableCell>
                      <TableCell align="right">{t('price')}</TableCell>
                      <TableCell align="right">{t('quantity')}</TableCell>
                      <TableCell align="center">{t('actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredQuotes?.map((quote) => (
                      <TableRow key={quote.productID}>
                        <TableCell>
                          <Box sx={{
                            width: '60px',
                            height: '60px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'background.default',
                            borderRadius: 1,
                            overflow: 'hidden',
                            cursor: quote.photo ? 'pointer' : 'default',
                            '&:hover': {
                              opacity: quote.photo ? 0.8 : 1,
                            }
                          }}
                            onClick={() => quote.photo && handleImageClick(quote.photo, quote.productDesc)}
                          >
                            {quote.photo ? (
                              <img
                                src={quote.photo}
                                alt={quote.productDesc}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain'
                                }}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = '/assets/chekup/tire.png';
                                }}
                              />
                            ) : (
                              <InventoryIcon sx={{ color: 'text.secondary' }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {quote.productID}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                              {quote.brandID} • {quote.quality}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {quote.productDesc}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              fontSize: '0.7rem'
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.7rem' }}>
                                <InventoryIcon sx={{ fontSize: '0.7rem' }} />
                                {quote?.quantityAvail || t('notAvailable')}
                              </Box>
                              {quote.speedIndex && (
                                <>
                                  •
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.7rem' }}>
                                    {quote.speedIndex}
                                  </Box>
                                </>
                              )}
                              {quote.extraLoad && (
                                <>
                                  •
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.7rem' }}>
                                    XL
                                  </Box>
                                </>
                              )}
                              {quote.orderByTime && (
                                <>
                                  •
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.7rem' }}>
                                    <AccessTimeIcon sx={{ fontSize: '0.7rem' }} />
                                    {moment(quote?.orderByTime).format('DD MMM HH:mm')}
                                  </Box>
                                </>
                              )}
                              {quote.dispatchTime && (
                                <>
                                  •
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.7rem' }}>
                                    <LocalShippingIcon sx={{ fontSize: '0.7rem' }} />
                                    {moment(quote?.dispatchTime).format('DD MMM HH:mm')}
                                  </Box>
                                </>
                              )}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {quote?.listPrice?.toFixed(2)}$
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                              {quote?.price?.toFixed(2)}$

                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleQuantityChange(quote.productID, -1)}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="body2" sx={{ minWidth: '30px', textAlign: 'center' }}>
                              {quantities[quote.productID] || 1}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleQuantityChange(quote.productID, 1)}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleAddToCart(quote)}
                            title={t('addToCart')}
                          >
                            <AddShoppingCartIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
        {/* Cart Section - 1/3 width */}
        {cart?.length > 0 && (
          <Grid item xs={12} md={4} sx={{ height: '40vh' }}>
            <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: businessPreference?.mainColor, borderRadius: 2, backgroundColor: businessPreference?.mainColor + '05', height: '40vh' }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>{t('cart')}</Typography>
                  <Typography variant="caption" sx={{
                    bgcolor: businessPreference?.mainColor || 'primary.main',
                    color: 'white !important',
                    px: 0.75,
                    py: 0,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}>
                    {cart.length}
                  </Typography>
                </Box>
              </Box>
              <TableContainer sx={{ maxHeight: '27vh', overflowY: 'auto', backgroundColor: 'transparent' }}>
                <Table size="small">
                  <TableBody>
                    {cart.map((item, index) => (
                      <TableRow key={`${item.productID}-${index}`}>
                        <TableCell>
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {item.productID}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {item?.totalPrice?.toFixed(2)} $
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {item.productDesc}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  {t('qty')}: {item.quantity}
                                </Typography>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveFromCart(index)}
                                  title={t('removeFromCart')}
                                  sx={{ padding: '2px' }}
                                >
                                  <DeleteIcon sx={{ fontSize: '0.875rem' }} />
                                </IconButton>
                              </Box>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {cart.length > 0 && (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 1,
                  pt: 1,
                  px: 2
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.85rem', marginRight: '1px' }}>
                    {t('subtotal')}: {cart.reduce((sum, item) => sum + item.totalPrice, 0)?.toFixed(2)} $
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>
      <ImageModal
        open={!!selectedImage}
        onClose={handleCloseModal}
        imageUrl={selectedImage?.url}
        alt={selectedImage?.alt}
      />
    </>
  );
};

const Orders = ({ elementDetails, cart, setCart, setSelectedConnector, selectedConnector, handleSubmitAddCard, handleSubmitOrder }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sideTabValue, setSideTabValue] = useState(0);
  const [skuInput, setSkuInput] = useState('');
  const [searchQueryInput, setSearchQueryInput] = useState('');
  const [vlnSearchInput, setVlnSearchInput] = useState('');
  const [quantities, setQuantities] = useState({});
  const [quotesSearchInput, setQuotesSearchInput] = useState('');
  const [token, setToken] = useState('');

  const businessPreference = useSelector((state) => state.core.businessData);
  const singleElementDetails = useSelector(
    (state) => state.element.singleElementDetails
  );

  const getToken = async () => {
    const response = await nodeAxiosFirebase({
      t,
      method: 'POST',
      url: 'https://extensionsext-kjeoh6r3gq-nn.a.run.app/worldpac',
      body: {
        data: {
          nomFonction: 'getToken',
        }
      }
    });
    if (response) {
      setToken(response.token);
    }
  }


  useEffect(() => {
    getToken();
  }, []);

  const handleGetData = async (productId) => {
    const currentConnector = connectors[sideTabValue];
    if (!currentConnector?.apiDetails?.getInventory) {
      setData(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setData(null);
    try {

      const response = await nodeAxiosFirebase({
        t,
        method: 'POST',
        showLoading: false,
        url: currentConnector.apiDetails.getInventory.url,
        body: {
          data: {
            nomFonction: currentConnector.apiDetails.getInventory.function,
            body: currentConnector?.name === 'VLN' ? {
              strRechercher: vlnSearchInput,
              quantity: 1,
              productID: null,
              paramFonction: {
                strRechercher: vlnSearchInput
              }
            } : {
              strRechercher: '',
              quantity: 1,
              productID: productId || currentConnector.apiDetails.getInventory.defaultProductId,
            }
          }
        },
      });

      if (response && response.errorData) {
        console.error("API Error:", response.errorData);
        setData(null);
      } else if (response) {
        if (currentConnector?.name === 'VLN') {
          if (response.parsedResult && Object.keys(response.parsedResult).length > 0) {
            const tireData = Object.entries(response.parsedResult).map(([id, item]) => {
              const tire = {
                productID: id,
                productDesc: item.description,
                brandID: item.manufacturer || item.loadIndex,
                quality: item.tireType,
                costPrice: item.costPrice || 0,
                internalPrice: item.internalPrice || 0,
                quantityAvail: parseInt(item.totalQty || 0),
                speedIndex: item.speedIndex,
                loadIndex: item.loadIndex,
                extraLoad: item.extraLoad === '1',
                runFlat: item.runFlat === '1',
                studdable: item.studdable === '1',
                roadHazard: item.roadHazard === '1',
                photo: item.photo ? 'https://cylabh.com/partage/Medias/images/photos' + item.photo : '/assets/chekup/tire.png',
                lastUpdate: item.lastUpdate
              };

              return tire;
            });
            const standardizedData = {
              ...response,
              quotes: (tireData || []).map(quote => standardizeQuote(quote, currentConnector))
            };

            setData(standardizedData);
          } else {

            setData({ quotes: [] });
          }
        } else {
          const standardizedData = {
            ...response,
            quotes: (response.quotes || []).map(quote => standardizeQuote(quote, currentConnector))
          };
          setData(standardizedData);
        }
      } else {
        console.error("Invalid data structure from API or non-200 status:", response);
        setData(null);
      }
    } catch (err) {
      console.error("Error fetching quotes:", err.message);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSideTabChange = (event, newValue) => {
    const connector = connectors[newValue];
    setSideTabValue(newValue);
    setSelectedConnector(connector);
    setData(null);
    setSkuInput('');
    setSearchQueryInput('');
    setVlnSearchInput('');
    setQuotesSearchInput('');
    setQuantities({});
    setCart([]);
    setIsLoading(false);
  };

  const connectors = [
    {
      name: 'WorldPAC',
      logo: <img src="/assets/chekup/worldpac.png" alt="WorldPAC" style={{ width: '24px', height: '24px', objectFit: 'contain' }} />,
      storeUrl: 'https://speeddial.worldpac.com/#/login',
      description: t('worldpacDescription'),
      apiDetails: {
        token: token,
        getInventory: {
          url: 'https://extensionsext-kjeoh6r3gq-nn.a.run.app/worldpac',
          function: 'GetProductQuotes',
          defaultProductId: ''
        },
      }
    },
    {
      name: 'VLN',
      logo: <img src="/assets/chekup/vln.png" alt="VLN" style={{ width: '24px', height: '24px', objectFit: 'contain' }} />,
      description: t('vlnDescription'),
      storeUrl: 'http://distributionvln.com/',
      apiDetails: {
        getInventory: {
          url: 'https://extensionsext-kjeoh6r3gq-nn.a.run.app/vln',
          function: 'getInventory',
          defaultProductId: ''
        },
      }
    },
  ];

  const handleQuantityChange = (productId, change) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: Math.max(1, (prev[productId] || 1) + change)
    }));
  };

  const handleAddToCart = (quote) => {
    const quantity = quantities[quote.productID] || 1;
    const cartItem = {
      ...quote,
      quantity,
      totalPrice: quote.price * quantity
    };
    setCart(prev => [...prev, cartItem]);
  };

  const handleRemoveFromCart = (index) => {
    setCart(prev => prev.filter((_, i) => i !== index));
  };



  const renderContent = () => {
    const currentConnector = connectors[sideTabValue];
    if (!currentConnector) return <Typography p={3}>{t('Please select a connector.')}</Typography>;

    if (currentConnector.name === 'WorldPAC') {
      return (
        <Box p={3} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper elevation={0} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div className='row middle-content'>
              <div className='col-9 gap-2'>
                <MuiTextField
                  label={t('searchTermCatalog')}
                  variant="outlined"
                  value={searchQueryInput}
                  onChange={(e) => setSearchQueryInput(e.target.value)}
                  size="small"
                  sx={{ flexGrow: 1 }}
                />
              </div>
              <div className='col-3'>
                <Button
                  label={t('search')}
                  fullWidth
                  onClick={() => {
                    if (searchQueryInput) {
                      const searchUrl = `https://speeddial.worldpac.com/punchout/v1/ShowCatalog?token=${token}&smsVendorName=Node&searchTerm=${encodeURIComponent(searchQueryInput)}&vin=${singleElementDetails?.targetProfileDetails?.name || ''}&referenceID=${singleElementDetails?.id}`;
                      window.open(searchUrl, '_blank');
                    }
                  }}
                  disabled={!searchQueryInput}
                />
              </div>
            </div>
          </Paper>

          <Paper elevation={0} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1">{t('getPricesSku')}</Typography>
            <div className='row middle-content'>
              <div className='col-9 gap-2'>
                <MuiTextField
                  label={t('enterSku')}
                  variant="outlined"
                  value={skuInput}
                  onChange={(e) => setSkuInput(e.target.value)}
                  size="small"
                  sx={{ flexGrow: 1 }}
                />
              </div>
              <div className='col-3'>
                <Button
                  label={t('getPrices')}
                  fullWidth
                  onClick={() => handleGetData(skuInput)}
                  disabled={!skuInput || isLoading}
                />
              </div>
            </div>
          </Paper>

          {isLoading && <Box sx={{ display: 'flex', py: 6, justifyContent: 'center', alignItems: 'center', height: '100%' }}><Loading type='circle' /></Box>}
          {!isLoading && data && (
            <ProductList
              data={data}
              cart={cart}
              quantities={quantities}
              quotesSearchInput={quotesSearchInput}
              setQuotesSearchInput={setQuotesSearchInput}
              handleQuantityChange={handleQuantityChange}
              handleAddToCart={handleAddToCart}
              handleRemoveFromCart={handleRemoveFromCart}
              businessPreference={businessPreference}
              t={t}
            />
          )}
          {!isLoading && !data && skuInput && (
            <Blocks empty={true} emptyType={'ListData'} emptyMessage={t('searchToGet')} noBorder noPadding heightPercentage={30}></Blocks>
          )}
        </Box>
      );
    }

    // Default content for other connectors
    return (
      <Box p={3}>
        {currentConnector.name === 'VLN' && (
          <>
            <Paper elevation={0} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, mt: 0 }}>
              <Typography variant="subtitle1">{t('searchTires')}</Typography>
              <div className='row middle-content'>
                <div className='col-9 gap-2'>
                  <MuiTextField
                    label={t('enterTireSearch')}
                    variant="outlined"
                    value={vlnSearchInput}
                    onChange={(e) => setVlnSearchInput(e.target.value)}
                    size="small"
                    sx={{ flexGrow: 1 }}
                  />
                </div>
                <div className='col-3'>
                  <Button
                    label={t('search')}
                    fullWidth
                    onClick={() => handleGetData()}
                    disabled={!vlnSearchInput || isLoading}
                  />
                </div>
              </div>
            </Paper>

            {isLoading && <Box sx={{ display: 'flex', py: 6, justifyContent: 'center', alignItems: 'center', height: '100%' }}><Loading type='circle' /></Box>}
            {!isLoading && data && (
              <ProductList
                data={data}
                cart={cart}
                quantities={quantities}
                quotesSearchInput={quotesSearchInput}
                setQuotesSearchInput={setQuotesSearchInput}
                handleQuantityChange={handleQuantityChange}
                handleAddToCart={handleAddToCart}
                handleRemoveFromCart={handleRemoveFromCart}
                businessPreference={businessPreference}
                t={t}
              />
            )}
            {!isLoading && !data && vlnSearchInput && (
              <Blocks empty={true} emptyType={'ListData'} emptyMessage={t('searchToGet')} noBorder noPadding heightPercentage={30}></Blocks>
            )}
          </>
        )}
        {currentConnector.name !== 'VLN' && (
          <Typography>{t('Connector specific content goes here.')}</Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', typography: 'body1' }}>
      <Grid container spacing={0}>
        <Grid item xs={12} sm={4} md={3} sx={{ borderRight: 1, borderColor: 'divider' }}>
          <Tabs
            orientation="vertical"
            variant="scrollable"
            value={sideTabValue}
            onChange={handleSideTabChange}
            aria-label="External connectors tabs"
            sx={{
              borderRight: 1,
              borderColor: 'divider',
              width: '100%',
              '& .MuiTab-root': {
                alignItems: 'flex-start',
                padding: "16px 20px",
                minHeight: 'auto',
                textTransform: 'none',
                gap: '8px',
                width: '100%',
                justifyContent: 'flex-start',
              },
              '& .MuiTabs-indicator': {
                left: 0,
              },
              '& .MuiTab-iconWrapper': {
                marginRight: '12px',
              }
            }}
          >
            {connectors?.map((connector, index) => (
              <Tab
                key={index}
                icon={connector?.logo}
                iconPosition="start"
                label={
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start'
                  }}>
                    <Typography variant="body1">{connector?.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'left' }}>
                      {connector?.description}
                    </Typography>
                  </Box>
                }
                sx={{
                  justifyContent: 'flex-start',
                  '& img': {
                    marginRight: '8px',
                  }
                }}
              />
            ))}
          </Tabs>
        </Grid>
        <Grid item xs={12} sm={8} md={9}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 1,
              ml: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              backgroundColor: 'background.paper'
            }}
          >
            <Grid container spacing={1}>
              {/* Vehicle Details */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="subtitle2" color="primary" sx={{ minWidth: '60px' }}>
                    {t('vehicle')}:
                  </Typography>
                  <Typography variant="body2">
                    {singleElementDetails?.targetProfileDetails?.attribute1 || '-'} {' '}
                    {singleElementDetails?.targetProfileDetails?.attribute2 || '-'} {' '}
                    {singleElementDetails?.targetProfileDetails?.attribute3 || '-'} {' '}
                    ({singleElementDetails?.targetProfileDetails?.name || '-'})
                  </Typography>
                </Box>
              </Grid>

              {/* Client Details */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="subtitle2" color="primary" sx={{ minWidth: '60px' }}>
                      {t('client')}:
                    </Typography>
                    <Typography variant="body2">
                      {singleElementDetails?.targetName || '-'} {' '}
                      ({singleElementDetails?.targetPhone || '-'})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => window.open((selectedConnector?.storeUrl || connectors[sideTabValue]?.storeUrl), '_blank')}
                      title={t('openStore')}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {/* Add your settings navigation logic here */ }}
                      title={t('settings')}
                    >
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
          <Paper elevation={0} square sx={{ height: "100%" }}>
            {renderContent()}
          </Paper>
        </Grid>
      </Grid>

    </Box>
  );
};

export default Orders;